import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API_BASE } from '@/context/AuthContext';
import AppHeader from '@/components/AppHeader';
import {
  Plus,
  Search,
  RefreshCw,
  Edit3,
  Power,
  X,
  CheckCircle2,
  AlertCircle,
  Filter,
} from 'lucide-react';

/**
 * Generic admin management page shared between Referral Doctors and In-house Consultants.
 * Both categories have identical CRUD flows differing only in one text field (village vs specialty)
 * and the API path. Config-driven to keep them consistent while avoiding duplication.
 *
 * config = {
 *   title, subtitle, icon (JSX),
 *   entitySingular, entityPlural,
 *   apiPath: '/referral-doctors' | '/inhouse-consultants',
 *   extraField: { key: 'village' | 'specialty', label: 'Village' | 'Specialty', placeholder },
 *   testIdPrefix: 'referral-doctor' | 'consultant',
 * }
 */
export default function DirectoryManager({ config }) {
  const { getAuthHeader } = useAuth();
  const {
    title, subtitle, icon,
    entitySingular, entityPlural,
    apiPath,
    extraField,
    testIdPrefix,
    renderExtraBadges,
  } = config;

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    [extraField.key]: '',
    phone_number: '',
    status: 'active',
  });

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      const res = await axios.get(`${API_BASE}${apiPath}`, {
        headers: getAuthHeader(),
        params,
      });
      setRecords(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to load ${entityPlural.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, apiPath, entityPlural, getAuthHeader]);

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search / filter changes
  useEffect(() => {
    const t = setTimeout(fetchRecords, 350);
    return () => clearTimeout(t);
  }, [search, statusFilter, fetchRecords]);

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '', [extraField.key]: '', phone_number: '', status: 'active' });
    setShowForm(true);
    setError('');
  };

  const openEdit = (record) => {
    setEditing(record);
    setFormData({
      name: record.name || '',
      [extraField.key]: record[extraField.key] || '',
      phone_number: record.phone_number || '',
      status: record.status || 'active',
    });
    setShowForm(true);
    setError('');
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError(`${entitySingular} name is required.`);
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (editing) {
        await axios.put(`${API_BASE}${apiPath}/${editing.id}`, formData, {
          headers: getAuthHeader(),
        });
        setSuccess(`Updated ${entitySingular.toLowerCase()}: ${formData.name}`);
      } else {
        await axios.post(`${API_BASE}${apiPath}`, formData, { headers: getAuthHeader() });
        setSuccess(`Added ${entitySingular.toLowerCase()}: ${formData.name}`);
      }
      closeForm();
      fetchRecords();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to save ${entitySingular.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (record) => {
    setError('');
    setSuccess('');
    const nextStatus = record.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.patch(
        `${API_BASE}${apiPath}/${record.id}/status`,
        { status: nextStatus },
        { headers: getAuthHeader() }
      );
      setSuccess(`${record.name} marked as ${nextStatus}.`);
      fetchRecords();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update status.');
    }
  };

  const inputClass =
    'w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500';

  return (
    <div className="min-h-screen bg-slate-950 font-sans text-slate-100 flex flex-col">
      <AppHeader />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-400 flex items-center justify-center shrink-0">
              {icon}
            </div>
            <div>
              <h1
                data-testid={`${testIdPrefix}-page-title`}
                className="text-2xl sm:text-3xl font-bold text-white tracking-tight"
              >
                {title}
              </h1>
              <p className="text-slate-400 text-sm mt-1">{subtitle}</p>
            </div>
          </div>

          <button
            onClick={openCreate}
            data-testid={`add-${testIdPrefix}-btn`}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-slate-950 bg-teal-500 hover:bg-teal-400 rounded-xl shadow-md shadow-teal-500/20 transition-all"
          >
            <Plus className="w-4 h-4 stroke-[3]" />
            Add {entitySingular}
          </button>
        </div>

        {/* Alerts */}
        {success && (
          <div
            data-testid={`${testIdPrefix}-success-alert`}
            className="p-3 bg-emerald-950/60 border border-emerald-800/80 rounded-xl text-emerald-200 text-sm flex items-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{success}</span>
            <button
              onClick={() => setSuccess('')}
              className="ml-auto text-emerald-400/60 hover:text-emerald-200"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {error && !showForm && (
          <div
            data-testid={`${testIdPrefix}-error-alert`}
            className="p-3 bg-red-950/60 border border-red-800/80 rounded-xl text-red-200 text-sm flex items-center gap-2"
          >
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
            <span>{error}</span>
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-400/60 hover:text-red-200"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 flex flex-col md:flex-row items-stretch md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid={`${testIdPrefix}-search-input`}
              type="text"
              placeholder={`Search by name, ${extraField.label.toLowerCase()}, or phone…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
            />
          </div>

          <select
            data-testid={`${testIdPrefix}-status-filter`}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-950 border border-slate-800 px-3 py-2.5 rounded-xl text-sm text-slate-200 focus:outline-none focus:border-teal-500 min-w-[130px]"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={fetchRecords}
            data-testid={`${testIdPrefix}-refresh-btn`}
            className="p-2.5 bg-slate-950 hover:bg-slate-800 text-slate-300 border border-slate-800 rounded-xl transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="text-xs text-slate-400 flex items-center gap-2">
          <Filter className="w-3.5 h-3.5" />
          <span data-testid={`${testIdPrefix}-count`}>
            {loading ? 'Loading…' : `${records.length} ${records.length === 1 ? entitySingular.toLowerCase() : entityPlural.toLowerCase()} shown`}
          </span>
        </div>

        {/* Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table data-testid={`${testIdPrefix}-table`} className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-950/80 border-b border-slate-800 text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="py-3.5 px-4 font-semibold">{entitySingular}</th>
                  <th className="py-3.5 px-4 font-semibold">{extraField.label}</th>
                  <th className="py-3.5 px-4 font-semibold">Phone Number</th>
                  <th className="py-3.5 px-4 font-semibold">Status</th>
                  <th className="py-3.5 px-4 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-sm">
                {records.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-slate-500">
                      {loading ? 'Loading…' : `No ${entityPlural.toLowerCase()} match the current filters.`}
                    </td>
                  </tr>
                ) : (
                  records.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`${testIdPrefix}-row-${r.id}`}
                      className="hover:bg-slate-800/30 transition-colors"
                    >
                      <td className="py-4 px-4 align-top">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-teal-500/10 border border-teal-500/20 text-teal-300 flex items-center justify-center font-bold text-xs shrink-0">
                            {r.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="min-w-0">
                            <div className="text-white font-semibold">{r.name}</div>
                            <div className="text-[10px] text-slate-500 font-mono">ID: {r.id.slice(0, 8)}…</div>
                            {renderExtraBadges && (
                              <div className="mt-1 flex flex-wrap gap-1">{renderExtraBadges(r)}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 align-top text-slate-300">
                        {r[extraField.key] || <span className="text-slate-600">—</span>}
                      </td>
                      <td className="py-4 px-4 align-top text-slate-300 font-mono text-xs">
                        {r.phone_number || <span className="text-slate-600">—</span>}
                      </td>
                      <td className="py-4 px-4 align-top">
                        <span
                          data-testid={`${testIdPrefix}-status-${r.id}`}
                          className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${
                            r.status === 'active'
                              ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800/80'
                              : 'bg-rose-950/80 text-rose-300 border border-rose-800/80'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${r.status === 'active' ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                          {r.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 align-top text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => openEdit(r)}
                            data-testid={`edit-${testIdPrefix}-btn-${r.id}`}
                            className="p-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(r)}
                            data-testid={`toggle-${testIdPrefix}-status-btn-${r.id}`}
                            className={`p-1.5 text-xs rounded-lg border transition-colors ${
                              r.status === 'active'
                                ? 'bg-rose-950/40 text-rose-300 border-rose-800/60 hover:bg-rose-900/60'
                                : 'bg-emerald-950/40 text-emerald-300 border-emerald-800/60 hover:bg-emerald-900/60'
                            }`}
                            title={r.status === 'active' ? 'Deactivate' : 'Activate'}
                          >
                            <Power className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-[11px] text-slate-500 leading-relaxed">
          Note: Records are never permanently deleted. Use <b>Deactivate</b> to remove someone from selection lists while
          keeping historical referral records intact.
        </p>
      </main>

      {/* Add / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-lg rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center justify-between pb-4 border-b border-slate-800">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                {icon}
                {editing ? `Edit ${entitySingular}` : `Add ${entitySingular}`}
              </h3>
              <button onClick={closeForm} className="text-slate-400 hover:text-white" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div
                data-testid={`${testIdPrefix}-form-error`}
                className="mt-4 p-3 rounded-xl bg-red-950/50 border border-red-800/60 text-red-200 text-sm flex items-start gap-2"
              >
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                  {entitySingular} Name *
                </label>
                <input
                  data-testid={`${testIdPrefix}-form-name`}
                  type="text"
                  required
                  autoFocus
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={`e.g. Dr. ${entitySingular === 'Consultant' ? 'Meera Iyer' : 'Ramesh Kumar'}`}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                  {extraField.label}
                </label>
                <input
                  data-testid={`${testIdPrefix}-form-${extraField.key}`}
                  type="text"
                  value={formData[extraField.key]}
                  onChange={(e) => setFormData({ ...formData, [extraField.key]: e.target.value })}
                  placeholder={extraField.placeholder}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                  Phone Number
                </label>
                <input
                  data-testid={`${testIdPrefix}-form-phone`}
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="10-digit mobile — +91 or 0 prefix optional"
                  className={inputClass}
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Accepts formats like 9876543210, 09876543210, or +91 98765 43210.
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                  Status
                </label>
                <select
                  data-testid={`${testIdPrefix}-form-status`}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className={inputClass}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 text-sm text-slate-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid={`${testIdPrefix}-form-submit-btn`}
                  disabled={saving}
                  className="px-5 py-2.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl text-sm transition-all shadow-md shadow-teal-500/20 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editing ? `Update ${entitySingular}` : `Save ${entitySingular}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
